import { SETTINGS_PERMISSIONS } from '@/constants/permissions';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CatalogueListLayout } from '@/components/common/CatalogueListLayout';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { useLocalListSearch } from '@/hooks/useLocalListSearch';
import { getDateFromToFilterFields } from '@/utils/filterFields';
import { getTableEmptyText } from '@/utils/listSearch';
import { webUrlsQueryOptions } from '../api/webUrls.api';
import { useWebUrlActionModals } from '../hooks/useWebUrlActionModals';
import { useWebUrlFilters } from '../hooks/useWebUrlFilters';
import type { WebUrl } from '../types/webUrl.types';
import { toSingleSpacedText } from '../utils/webUrlText';
import { getWebUrlColumns } from './WebUrlColumns';
import { WebUrlDeleteConfirmation } from './WebUrlDeleteConfirmation';
import { WebUrlFormModal } from './WebUrlFormModal';

const PERIOD_FILTER_FIELDS = getDateFromToFilterFields();

const toSearchableValues = (webUrl: WebUrl) => [
  webUrl.title,
  webUrl.description === null ? null : toSingleSpacedText(webUrl.description),
  webUrl.actionLink,
];

export function WebUrlListTable() {
  const controller = useWebUrlFilters();
  const { params, appliedSearchText } = controller;

  const {
    modalState,
    webUrlToDelete,
    openCreateModal,
    openEditModal,
    closeModal,
    openDeleteModal,
    closeDeleteModal,
  } = useWebUrlActionModals();

  const { data, isLoading, error, refetch } = useQuery(webUrlsQueryOptions(params));

  const { searchText, setSearchText, visibleRows } = useLocalListSearch({
    rows: data,
    appliedText: appliedSearchText,
    onApply: controller.applySearchText,
    toSearchableValues,
  });

  const columns = useMemo(
    () =>
      getWebUrlColumns(appliedSearchText, {
        onEdit: openEditModal,
        onDelete: openDeleteModal,
      }),
    [appliedSearchText, openEditModal, openDeleteModal],
  );

  return (
    <CatalogueListLayout
      title="Web URLs"
      dateRange={{ dateFrom: params.activeFrom, dateTo: params.activeTo }}
      search={{
        value: searchText,
        onChange: setSearchText,
        placeholder: 'Title, description or link',
      }}
      filters={controller}
      filterFields={PERIOD_FILTER_FIELDS}
      onAdd={openCreateModal}
      addLabel="Add Web URL"
      addRequires={SETTINGS_PERMISSIONS.WEB_URLS_CREATE}
    >
      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load web URLs"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={visibleRows}
          columns={columns}
          isLoading={isLoading}
          stickyColumnId="title"
          className="lg:flex-1"
          {...getTableEmptyText(
            appliedSearchText,
            'No web URL is live in these dates',
            'Try a wider date range.',
          )}
        />
      )}

      <WebUrlFormModal
        webUrl={modalState?.webUrl ?? null}
        open={modalState !== null}
        onOpenChange={(open) => !open && closeModal()}
      />

      <WebUrlDeleteConfirmation
        webUrl={webUrlToDelete}
        open={webUrlToDelete !== null}
        onOpenChange={(open) => !open && closeDeleteModal()}
      />
    </CatalogueListLayout>
  );
}
